import { COLOR_FLYWEIGHT, formatData, getRankedColorsForCount } from '@utilities';

import { addSlideFooter } from '../slide_footer';
import { parseRichTextToPptRuns } from '../helpers.js';
import { FOOTER_TEXT, SURVEY_TITLE_OPTIONS, TABLE_BORDER, TEXT_COLOR } from '../constants';
const MAX_RANK_TABLE_ROWS_PER_SLIDE = 10;

/**
 * Gets rank chart data from question data
 * @param {Object} questionData
 * @param {String} displayAs
 * @returns {Object}
 */
const getRankChartData = (questionData, displayAs = 'pct') => {
  if (!questionData?.segments?.length) return null;
  const firstSegment = questionData.segments[0];
  const isCountDisplay = displayAs === 'count';
  const isPercentageDisplay = !isCountDisplay;

  const requiredChartData = isCountDisplay ? firstSegment?.chartCount : firstSegment?.chartPct;

  if (requiredChartData?.series && requiredChartData?.xAxis?.categories) {
    return processChartDataSimple(
      requiredChartData,
      requiredChartData.xAxis.categories,
      isPercentageDisplay,
      questionData
    );
  }

  if (firstSegment?.results?.length) {
    const categories = firstSegment.results.map((item) => item.title);
    const series = [];
    if (firstSegment.results[0]?.data) {
      const sampleData = firstSegment.results[0].data;

      const rankKeys = Object.keys(sampleData)
        .filter((key) => {
          if (key.match(/^\d{1,2}$/) && parseInt(key) <= 15) return true;
          if (key.match(/^rank_\d+$/)) return true;
          return false;
        })
        .sort((a, b) => {
          const aNum = parseInt(a.replace('rank_', ''));
          const bNum = parseInt(b.replace('rank_', ''));
          return aNum - bNum;
        });

      rankKeys.forEach((rankKey, index) => {
        const rankNumber = parseInt(rankKey.replace('rank_', '')) || index + 1;
        const rankData = firstSegment.results.map((item) => {
          const value = item.data?.[rankKey] || 0;
          return typeof value === 'number' ? value : parseFloat(value) || 0;
        });

        series.push({
          data: rankData,
          id: rankNumber,
          name: rankNumber.toString(),
        });
      });

      const chartDataFromResults = {
        series,
        xAxis: { categories },
      };

      return processChartDataSimple(
        chartDataFromResults,
        categories,
        isPercentageDisplay,
        questionData
      );
    }
  }

  return null;
};

const processChartDataSimple = (chartData, categories, isPercentageDisplay) => {
  const reversedCategories = [...categories].reverse();
  if (chartData.series) {
    chartData.series.forEach((series) => {
      if (series.data) {
        series.data.forEach((data, index) => {
          if (data === 'NAN_SENTINEL') {
            series.data[index] = null;
          }
        });
      }
    });
  }

  const series = chartData.series;

  const validSeries = series.filter((seriesItem) => {
    const hasData = (seriesItem.data || []).some((d) => {
      if (d === null || d === undefined) return false;
      if (typeof d === 'object' && d.high !== undefined && d.low !== undefined) {
        return Math.abs(d.high - d.low) > 0;
      }
      return Math.abs(parseFloat(d) || 0) > 0;
    });
    return hasData;
  });
  const sortedSeries = [...validSeries].sort((a, b) => {
    const getRankNumber = (item) => {
      if (item.id && !isNaN(parseInt(item.id))) {
        return parseInt(item.id);
      }
      if (item.name) {
        const match = item.name.match(/(\d+)/);
        if (match) {
          return parseInt(match[1]);
        }
      }
      return 0;
    };

    return getRankNumber(b) - getRankNumber(a);
  });

  const totalSeries = sortedSeries.length;
  const leftSideCount = Math.floor(totalSeries / 2);

  const dataSeries = sortedSeries.map((seriesItem, seriesIndex) => {
    const dataArray = (seriesItem.data || []).map((value) => {
      if (value === 'NAN_SENTINEL' || value === null || value === undefined) {
        return 0;
      }

      // Data from convertChartToColumnRange is always an object with {high, low}
      const span = value.high - value.low;
      let numericValue = seriesIndex < leftSideCount ? -span : span;

      if (isNaN(numericValue)) {
        return 0;
      }

      if (isPercentageDisplay) {
        if (Math.abs(numericValue) > 2) {
          numericValue = numericValue / 100;
        }
      }

      return numericValue;
    });

    const getRankNumber = (item) => {
      if (item.id && !isNaN(parseInt(item.id))) {
        return parseInt(item.id);
      }
      if (item.name) {
        const match = item.name.match(/(\d+)/);
        if (match) {
          return parseInt(match[1]);
        }
      }
      return seriesIndex + 1;
    };

    const rankNumber = getRankNumber(seriesItem);
    const seriesName = (seriesItem.name || `${rankNumber}`).replace(/\s*\((LOW|HIGH)\)\s*/, '');

    const divergingData = dataArray.reverse();

    return {
      color: null,
      labels: reversedCategories,
      name: seriesName,
      values: divergingData,
    };
  });

  const actualValidSeriesCount = validSeries.length;
  const allColors = getRankedColorsForCount(actualValidSeriesCount);

  const coloredDataSeries = dataSeries.map((series, index) => {
    const seriesName = series.name;
    const rankNumber = parseInt(seriesName) || index + 1;

    const totalRanks = actualValidSeriesCount;
    const colorIndex = totalRanks - rankNumber;

    const assignedColor = allColors[colorIndex] || allColors[0];
    return {
      ...series,
      color: assignedColor,
      name: rankNumber.toString(),
    };
  });

  const negativeSeriesData = coloredDataSeries.slice(0, leftSideCount);
  const positiveSeries = coloredDataSeries.slice(leftSideCount);
  const reversedNegative = [...negativeSeriesData].reverse();
  const chartOrderedData = [...reversedNegative, ...positiveSeries];

  return {
    categoryLabels: reversedCategories,
    chartOptions: {
      barDir: 'bar',
      barGrouping: 'stacked',
      showLegend: false,
    },
    dataSeries: chartOrderedData,
  };
};

/**
 * Creates a ranking chart slide using PptxGenJS addChart
 * @param {Object} options
 * @param {String} options.compareBy
 * @param {String} options.displayAs
 * @param {Object} options.pptx
 * @param {Object} options.questionData
 * @param {String} options.questionNumber
 * @param {String} options.questionTitle
 * @param {String} options.reportType
 * @param {String} options.reportUrl
 * @param {String} options.title
 * @returns {Object}
 */
export const createRankChartSlide = ({
  compareBy,
  displayAs = 'pct',
  pptx,
  questionData,
  questionNumber,
  questionTitle,
  reportType,
  reportUrl,
  title,
}) => {
  if (!questionTitle || questionTitle.length < 3) {
    return null;
  }

  const shouldCreateMultipleSlides =
    compareBy?.value === 'quota_group' ||
    (compareBy?.value && compareBy.value !== 'overall' && questionData?.segments?.length > 1);

  if (shouldCreateMultipleSlides && questionData?.segments?.length > 1) {
    const maxSlides = Math.min(questionData.segments.length, 10);

    for (let i = 0; i < maxSlides; i++) {
      const segment = questionData.segments[i];
      const singleSegmentData = {
        ...questionData,
        segments: [segment],
      };

      const chartData = getRankChartData(singleSegmentData, displayAs);
      if (!chartData) {
        continue;
      }

      const segmentName = segment.name || segment.id;

      createSingleRankSlide({
        chartData,
        compareBy,
        displayAs,
        pptx,
        questionNumber,
        questionTitle,
        reportType,
        reportUrl,
        segmentName,
        title,
      });
    }
  } else {
    const chartData = getRankChartData(questionData, displayAs);
    if (!chartData) {
      return null;
    }

    createSingleRankSlide({
      chartData,
      compareBy,
      displayAs,
      pptx,
      questionNumber,
      questionTitle,
      reportType,
      reportUrl,
      segmentName: null,
      title,
    });
  }
};

/**
 * Creates a single rank chart slide
 */
const createSingleRankSlide = ({
  chartData,
  compareBy,
  displayAs,
  pptx,
  questionNumber,
  questionTitle,
  reportType,
  reportUrl,
  segmentName,
  title,
}) => {
  const questionTitleRuns = parseRichTextToPptRuns(questionTitle);
  if (!questionTitleRuns.length) return null;

  const slide = pptx.addSlide();

  const questionPrefix = questionNumber ? `Q${questionNumber}. ` : '';
  const compareByLabel = compareBy?.label || compareBy?.value || compareBy;
  const isOverall = compareByLabel?.toLowerCase() === 'overall';

  const titleRuns = [
    ...(questionPrefix ? [{ text: questionPrefix }] : []),
    ...questionTitleRuns,
    ...(compareByLabel ? [{ text: ` Compared by: ${compareByLabel}` }] : []),
    ...(segmentName && !isOverall ? [{ text: ` - ${segmentName}` }] : []),
  ];

  const titleOptions = SURVEY_TITLE_OPTIONS;
  slide.addText(titleRuns, titleOptions);

  const chartYPosition = titleOptions.h > 1.0 ? 1.2 : 0.9;
  const chartHeight = titleOptions.h > 1.0 ? '72%' : '74%';
  const chartWidth = '98%';
  const chartXPosition = 0.05;

  try {
    const cleanedChartData = chartData.dataSeries.map((series) => ({
      ...series,
      values: series.values.map((value) => {
        if (value === null || value === undefined || isNaN(value)) {
          return 0;
        }
        const cleaned = Math.round(value * 1000) / 1000;
        if (Math.abs(cleaned) < 0.001) {
          return 0;
        }
        return cleaned;
      }),
    }));

    const isPercentageDisplay = displayAs === 'pct' || displayAs === 'percentage';

    const chartOptions = {
      barDir: 'bar',
      barGrouping: 'stacked',
      catAxisLabelFontSize: 9,
      catAxisLabelPos: 'low',
      chartColors: cleanedChartData.map((series) => series.color),
      h: chartHeight,
      showCatAxisTitle: false,
      showLegend: false,
      w: chartWidth,
      x: chartXPosition,
      y: chartYPosition,
      ...(isPercentageDisplay && {
        valAxisLabelFormatCode: '0%',
        valAxisMajorUnit: 0.2,
        valAxisMaxVal: 1.0,
        valAxisMinVal: -1.0,
      }),
    };

    slide.addChart(pptx.charts.BAR, cleanedChartData, chartOptions);
  } catch (error) {
    slide.addText('Ranking chart could not be created', {
      color: TEXT_COLOR,
      fontSize: 12,
      h: 1,
      w: chartWidth,
      x: chartXPosition,
      y: chartYPosition,
    });
  }
  addSlideFooter({ pptx, reportType, reportUrl, slide });
  if (title) {
    slide.addText(
      [{ text: 'Source: ' }, { options: { hyperlink: { url: reportUrl } }, text: title }],
      FOOTER_TEXT
    );
  }

  return slide;
};

/**
 * Gets rank table data from question data
 * @param {Object} questionData
 * @param {String} displayAs
 * @param {String} segmentId
 * @returns {Object}
 */
const getRankTableData = (questionData, displayAs = 'pct', segmentId = null) => {
  const segment = segmentId
    ? questionData.segments.find((s) => s.id === segmentId)
    : questionData.segments[0];
  const isCountDisplay = displayAs === 'count';
  const rawTableData = isCountDisplay ? segment.tableCount : segment.tablePct;

  return rawTableData;
};

/**
 * Creates a single rank table slide
 * @param {Object} options
 * @returns {Object}
 */
const createSingleRankTableSlide = ({
  pptx,
  questionNumber,
  questionTitle,
  reportType,
  reportUrl,
  slideSuffix,
  tableData,
  title,
}) => {
  const slide = pptx.addSlide();

  const questionTitleRuns = parseRichTextToPptRuns(questionTitle);
  if (!questionTitleRuns.length) return null;

  const questionPrefix = questionNumber ? `Q${questionNumber}. ` : '';

  const titleRuns = [
    ...(questionPrefix ? [{ text: questionPrefix }] : []),
    ...questionTitleRuns,
    ...(slideSuffix ? [{ text: slideSuffix }] : []),
  ];

  const titleOptions = SURVEY_TITLE_OPTIONS;
  slide.addText(titleRuns, titleOptions);

  const { columns, data } = tableData;

  const headerRow = columns.map((col) => ({
    options: {
      bold: true,
      color: TEXT_COLOR,
      fill: { color: COLOR_FLYWEIGHT },
      fontSize: 11,
    },
    text: col.Header || col.accessor,
  }));

  const dataRows = data.slice(0, MAX_RANK_TABLE_ROWS_PER_SLIDE).map((row) => {
    return columns.map((col, colIndex) => {
      const accessor = col.accessor || col.accessorKey;
      const value = row[accessor];
      let formattedValue = value;

      if (col.format && typeof value === 'number') {
        formattedValue = formatData({ format: col.format, value });
      }

      return {
        options: {
          align: colIndex === 0 ? 'left' : 'center',
          color: TEXT_COLOR,
          fontSize: 11,
          margin: [2, 3, 2, 3],
        },
        text: formattedValue?.toString() || '',
      };
    });
  });

  const tableRows = [headerRow, ...dataRows];

  // Calculate table dimensions based on title height and footer position
  const tableY = titleOptions.h > 1.0 ? 1.6 : 1.3;
  const footerY = FOOTER_TEXT.y || 5.15;
  const tableHeight = footerY - tableY - 0.3;
  const tableWidth = 9;
  const tableX = 0.5;

  // Calculate column widths: wider first column for labels, distribute rest evenly
  const firstColumnWidth = Math.min(3.0, tableWidth * 0.35);
  const remainingWidth = tableWidth - firstColumnWidth;
  const otherColumnWidth =
    columns.length > 1 ? remainingWidth / (columns.length - 1) : remainingWidth;
  const colWidths = [firstColumnWidth, ...Array(columns.length - 1).fill(otherColumnWidth)];

  try {
    slide.addTable(tableRows, {
      autoPage: false,
      autoPageHeaderRows: 1,
      autoPageRepeatHeader: true,
      border: TABLE_BORDER,
      color: TEXT_COLOR,
      colW: colWidths,
      fontSize: 11,
      h: tableHeight,
      margin: [2, 3, 2, 3],
      rowH: 0.3,
      w: tableWidth,
      x: tableX,
      y: tableY,
    });
  } catch (error) {
    slide.addText('Ranking table could not be created', {
      color: TEXT_COLOR,
      fontSize: 12,
      h: 1,
      w: tableWidth,
      x: tableX,
      y: tableY,
    });
  }

  addSlideFooter({ pptx, reportType, reportUrl, slide });
  if (title) {
    slide.addText(
      [{ text: 'Source: ' }, { options: { hyperlink: { url: reportUrl } }, text: title }],
      FOOTER_TEXT
    );
  }

  return slide;
};

/**
 * Creates rank table slides
 * @param {Object} options
 * @param {String} options.compareBy
 * @param {String} options.displayAs
 * @param {Object} options.pptx
 * @param {Object} options.questionData
 * @param {Number} options.questionNumber
 * @param {String} options.questionTitle
 * @param {String} options.reportType
 * @param {String} options.reportUrl
 * @param {String} options.title
 * @returns {Object}
 */
export const createRankTableSlide = ({
  compareBy,
  displayAs = 'pct',
  pptx,
  questionData,
  questionNumber,
  questionTitle,
  reportType,
  reportUrl,
  title,
}) => {
  const compareByLabel = compareBy?.label || compareBy?.value || compareBy;
  const baseSlideSuffix = compareByLabel ? ` Compared by: ${compareByLabel}` : '';

  if (questionData?.segments?.length > 1) {
    const maxSlides = Math.min(questionData.segments.length, 10);
    for (let i = 0; i < maxSlides; i++) {
      const segment = questionData.segments[i];
      const tableData = getRankTableData(questionData, displayAs, segment.id);

      if (!tableData) {
        continue;
      }

      if (tableData.data.length > MAX_RANK_TABLE_ROWS_PER_SLIDE) {
        let slideIndex = 1;
        for (
          let startRow = 0;
          startRow < tableData.data.length;
          startRow += MAX_RANK_TABLE_ROWS_PER_SLIDE
        ) {
          const endRow = Math.min(startRow + MAX_RANK_TABLE_ROWS_PER_SLIDE, tableData.data.length);
          const splitData = {
            ...tableData,
            data: tableData.data.slice(startRow, endRow),
          };

          const slideSuffix =
            tableData.data.length > MAX_RANK_TABLE_ROWS_PER_SLIDE
              ? `${baseSlideSuffix} - ${segment.name} (${slideIndex})`
              : `${baseSlideSuffix} - ${segment.name}`;

          createSingleRankTableSlide({
            pptx,
            questionNumber,
            questionTitle,
            reportType,
            reportUrl,
            slideSuffix,
            tableData: splitData,
            title,
          });

          slideIndex++;
        }
      } else {
        createSingleRankTableSlide({
          pptx,
          questionNumber,
          questionTitle,
          reportType,
          reportUrl,
          slideSuffix: `${baseSlideSuffix} - ${segment.name}`,
          tableData: tableData,
          title,
        });
      }
    }
  } else {
    const tableData = getRankTableData(questionData, displayAs);

    if (!tableData) {
      return null;
    }

    if (tableData.data.length > MAX_RANK_TABLE_ROWS_PER_SLIDE) {
      let slideIndex = 1;
      for (
        let startRow = 0;
        startRow < tableData.data.length;
        startRow += MAX_RANK_TABLE_ROWS_PER_SLIDE
      ) {
        const endRow = Math.min(startRow + MAX_RANK_TABLE_ROWS_PER_SLIDE, tableData.data.length);
        const splitData = {
          ...tableData,
          data: tableData.data.slice(startRow, endRow),
        };

        const slideSuffix =
          tableData.data.length > MAX_RANK_TABLE_ROWS_PER_SLIDE
            ? `${baseSlideSuffix} (${slideIndex})`
            : baseSlideSuffix;

        createSingleRankTableSlide({
          pptx,
          questionNumber,
          questionTitle,
          reportType,
          reportUrl,
          slideSuffix,
          tableData: splitData,
          title,
        });

        slideIndex++;
      }
    } else {
      createSingleRankTableSlide({
        pptx,
        questionNumber,
        questionTitle,
        reportType,
        reportUrl,
        slideSuffix: baseSlideSuffix,
        tableData: tableData,
        title,
      });
    }
  }

  return null;
};
